import { InfoIcon } from '@/assets/icons/info-icons/info';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import { Accordion } from '@/components/basicComponents/accordion';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import { useResponsive } from '@/hooks/use-responsive';
import { IFieldEditor } from '@/shared/types/fieldEditor';
import { themeVar } from '@/styles/theme';
import styled from 'styled-components';

interface AiPromptSectionProps {
  currentField: IFieldEditor;
  openPromptEditorModalPage: () => void;
}

export const AiPromptSection = ({ currentField, openPromptEditorModalPage }: AiPromptSectionProps) => {
  const { isMobile } = useResponsive();

  return (
    <Accordion title="AI Prompt" onEdit={openPromptEditorModalPage}>
      <Banner
        title=""
        description={
          <>
            These settings provide instructions for the AI to suggest an answer for this <b>{currentField?.name || ''}</b> field based on provided
            files. Changes here will impact all future AI suggestions for this field for both this record and any other record.
          </>
        }
        icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
        bgColor={themeVar('layer.base')}
        color={themeVar('font.base')}
        dividerColor={themeVar('border.base')}
      />
      {!currentField?.promptGroup?.prompts?.[0] &&
      !currentField?.promptGroup?.useCustomContextPrompt &&
      !currentField?.promptGroup?.useCustomContextPrompt ? (
        <SectionZeroState>
          <SectionZeroStateText>No prompt has been configured for this field. It is currently ignored by the AI Assistant.</SectionZeroStateText>
          <Button
            onClick={openPromptEditorModalPage}
            styleType="outline"
            icon={<AddButtonIcon fill={themeVar('action.default')} />}
            size="md"
            width="max-content"
          >
            Add Prompt
          </Button>
        </SectionZeroState>
      ) : (
        <AiPromptFields>
          <TwoColumnWrapper isMobile={isMobile}>
            <AiPromptItem>Prompt</AiPromptItem>
            <AiPromptItem isBold>{currentField?.promptGroup?.prompts?.[0] ?? ''}</AiPromptItem>
          </TwoColumnWrapper>
          <TwoColumnWrapper isMobile={isMobile}>
            <AiPromptItem>Context Template</AiPromptItem>
            <AiPromptItem isBold>
              {currentField?.promptGroup.useCustomContextPrompt ? currentField?.promptGroup?.contextPrompt : 'Use default'}
            </AiPromptItem>
          </TwoColumnWrapper>
          <TwoColumnWrapper isMobile={isMobile}>
            <AiPromptItem>Summary Template</AiPromptItem>
            <AiPromptItem isBold>
              {currentField?.promptGroup?.useCustomSummaryPrompt ? currentField?.promptGroup?.summaryPrompt : 'Use default'}
            </AiPromptItem>
          </TwoColumnWrapper>
          <TwoColumnWrapper isMobile={isMobile}>
            <AiPromptItem>Structured Output</AiPromptItem>
            <AiPromptItem isBold>{currentField?.promptGroup?.outputStructure ? currentField?.promptGroup?.outputStructure : 'None'}</AiPromptItem>
          </TwoColumnWrapper>
        </AiPromptFields>
      )}
    </Accordion>
  );
};

const AiPromptFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 20px;
  width: 100%;
`;

const TwoColumnWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: 25% 1fr;
  gap: 20px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '60%')};
  padding: 2px 10px;
  width: 100%;
`;

const AiPromptItem = styled.div<{ isBold?: boolean }>`
  font-size: 16px;
  font-weight: ${({ isBold }) => (isBold ? 600 : 400)};
  line-height: 22.4px;
  color: ${themeVar('font.base')};
  padding: 4px;
`;

const SectionZeroState = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;
`;

const SectionZeroStateText = styled.div`
  padding: 10px 0;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: ${themeVar('font.weak')};
`;
